import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useDispatchApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import {
  startAddContentsToList,
  startBumpAndUpdateContentInList,
} from "../../actions/user";
import {
  markCreativeJobResultsPersisted,
  startPersistCreativeJobResults,
} from "../../actions/creativeJobs";
import {
  ADS_CAMPAIGN_CAROUSEL_FORMAT,
  ALERTS_ASYNC_STATUS,
  IMAGE_FORMATS,
  VIDEO_FORMATS,
} from "../../utils/constants";
import {
  CREATIVE_ALERT_MODAL_TYPE,
  getCreativeJobAlertConfig,
} from "../../utils/creativeJobAlertConfig";
import {
  jobHasBackendCreatedContents,
  resolveCreativeJobAlertPersistableUrls,
  resolveCreativeJobAlertResultUrls,
} from "../../utils/creativeJobResultUrls";
import { buildImageEditSessionFromJob } from "../../utils/buildImageEditSessionFromJob";
import { buildBatchImageEditSessionFromJob } from "../../utils/buildBatchImageEditSessionFromJob";
import {
  MODAL_BATCH_IMAGE_EDIT_VERSION_LABEL,
  MODAL_IMAGE_EDIT_EDITED_IMAGE_LABEL,
  MODAL_IMAGE_EDIT_PROCESSING_MESSAGE,
} from "../../i18n/keysTranslations";
import { buildCreateWithIAInitialValues } from "../Modal/ModalCreateWithIA/utils/generationPlaceholders";

const ModalCreateWithIA = lazy(() => import("../Modal/ModalCreateWithIA"));
const ModalGallery = lazy(() => import("../Modal/ModalGallery"));
const ModalImageEdit = lazy(() => import("../Modal/ModalImageEdit"));
const ModalBatchImageEdit = lazy(
  () => import("../Modal/ModalImageEdit/ModalBatchImageEdit")
);

const alertPersistPromises = new Map();

const getOrStartAlertPersist = ({ dispatch, alert }) => {
  const persistKey = `${alert?.jobID ?? alert?.id}:${alert?.status}`;

  if (!alertPersistPromises.has(persistKey)) {
    const persistPromise = persistGalleryResultsFromAlert({
      dispatch,
      alert,
    }).finally(() => {
      alertPersistPromises.delete(persistKey);
    });

    alertPersistPromises.set(persistKey, persistPromise);
  }

  return alertPersistPromises.get(persistKey);
};

const persistGalleryResultsFromAlert = async ({ dispatch, alert }) => {
  const config = getCreativeJobAlertConfig(alert?.jobType);
  if (config.persistFrom === "none") return;

  if (alert?.resultsPersistedAt) return;

  const generationData = alert?.generationData || {};
  const shouldSkipPersist =
    generationData.persistToContentList === true ||
    jobHasBackendCreatedContents({
      jobResponse: generationData.jobResponse,
      generationData,
    });

  if (shouldSkipPersist) {
    if (alert?.jobID) {
      await dispatch(markCreativeJobResultsPersisted({ jobID: alert.jobID }));
    }
    return;
  }

  const urls = resolveCreativeJobAlertPersistableUrls({
    jobType: alert?.jobType,
    generationData: alert?.generationData,
    jobResponse: alert?.generationData?.jobResponse,
    resultUrl: alert?.resultUrl,
    resultUrls: alert?.resultUrls,
  });

  if (urls.length === 0) return;

  if (alert?.jobID) {
    await dispatch(
      startPersistCreativeJobResults({
        jobID: alert.jobID,
        contents: urls.map((url) => ({
          url,
          from: config.persistFrom,
        })),
      })
    );
    return;
  }

  await dispatch(
    startAddContentsToList(
      urls.map((url) => ({
        url,
        from: config.persistFrom,
      }))
    )
  );
};

const GalleryFromCreativeAlert = ({ alert, onClose, userID }) => {
  const dispatch = useDispatchApp();
  const [isReady, setIsReady] = useState(
    alert?.status !== ALERTS_ASYNC_STATUS.SUCCESS
  );
  const jobID = alert?.jobID ?? alert?.id;
  const alertStatus = alert?.status;
  const resultsPersistedAt = alert?.resultsPersistedAt;

  useEffect(() => {
    let cancelled = false;

    const prepareGallery = async () => {
      if (alertStatus === ALERTS_ASYNC_STATUS.SUCCESS) {
        await getOrStartAlertPersist({ dispatch, alert });
      }

      if (!cancelled) {
        setIsReady(true);
      }
    };

    prepareGallery();

    return () => {
      cancelled = true;
    };
  }, [alert, dispatch, jobID, alertStatus, resultsPersistedAt]);

  if (!isReady) return null;

  return (
    <ModalGallery
      modalOpen={true}
      onCloseModal={onClose}
      multiple={true}
      isOutsideCampaign={true}
      acceptedFormats={IMAGE_FORMATS.concat(VIDEO_FORMATS).concat(
        ADS_CAMPAIGN_CAROUSEL_FORMAT
      )}
      pathStorage={`socialMedia/${userID}/`}
      modeUpload="sync"
      onCallbackSelectedItems={() => {}}
      closeActionsDisabled={true}
      enableCreativeJobAlertPlaceholders={true}
    />
  );
};

const ImageEditFromCreativeAlert = ({ alert, onClose, onOpenGallery }) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const generationData = alert?.generationData || {};
  const resultUrls = resolveCreativeJobAlertResultUrls({
    generationData,
    jobResponse: generationData.jobResponse,
    resultUrl: alert?.resultUrl,
    resultUrls: alert?.resultUrls,
  });
  const editedUrl = resultUrls[0] || null;
  const isInProgress =
    alert?.status === ALERTS_ASYNC_STATUS.LOADING ||
    alert?.status === ALERTS_ASYNC_STATUS.GENERATING;
  const builtSession = buildImageEditSessionFromJob({
    payloadSnapshot: generationData,
    response: editedUrl ? { data: { kind: "url", url: editedUrl } } : undefined,
    getEditedImageLabel: (version) =>
      t(MODAL_IMAGE_EDIT_EDITED_IMAGE_LABEL, { version }),
    processingMessage: t(MODAL_IMAGE_EDIT_PROCESSING_MESSAGE),
    isSuccess: Boolean(editedUrl),
    isInProgress,
    errorMessage: generationData.jobResponse?.message || alert?.message || null,
  });
  const session = {
    ...builtSession,
    hydrateKey: [
      alert?.jobID || "",
      alert?.status || "",
      isInProgress ? "1" : "0",
      builtSession.initialChatMessages.length,
      builtSession.initialResultImageUrl || "",
    ].join(":"),
  };

  return (
    <ModalImageEdit
      isOpen={true}
      onClose={onClose}
      initialImageUrl={session.initialImageUrl || session.url}
      initialImageEditSession={session}
      onCallbackSave={async (savePayload) => {
        const url =
          typeof savePayload === "string" ? savePayload : savePayload?.url;
        if (!url) return;

        const saved = await dispatch(
          startAddContentsToList([
            {
              url,
              from: "ai",
            },
          ])
        );
        if (!saved) return;

        onOpenGallery?.();
      }}
    />
  );
};

const BatchImageEditFromCreativeAlert = ({ alert, onClose, onOpenGallery }) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const generationData = alert?.generationData || {};
  const isSuccess = alert?.status === ALERTS_ASYNC_STATUS.SUCCESS;
  const isInProgress =
    alert?.status === ALERTS_ASYNC_STATUS.LOADING ||
    alert?.status === ALERTS_ASYNC_STATUS.GENERATING;
  const builtSession = buildBatchImageEditSessionFromJob({
    payloadSnapshot: generationData,
    response: generationData.jobResponse,
    getVersionLabel: (version) =>
      t(MODAL_BATCH_IMAGE_EDIT_VERSION_LABEL, { version }),
    processingMessage: t(MODAL_IMAGE_EDIT_PROCESSING_MESSAGE),
    isSuccess,
    isInProgress,
    errorMessage: generationData.jobResponse?.message || alert?.message || null,
  });
  const session = {
    ...builtSession,
    hydrateKey: [
      alert?.jobID || "",
      alert?.status || "",
      isInProgress ? "1" : "0",
      isSuccess ? "1" : "0",
      builtSession.initialChatMessages.length,
    ].join(":"),
  };

  const onApplyBatchImageEditResults = async ({
    mode,
    items: successfulItems,
  }) => {
    if (!Array.isArray(successfulItems) || successfulItems.length === 0) {
      return;
    }

    if (mode === "new") {
      const persistResult = await dispatch(
        startPersistCreativeJobResults({
          jobID: alert?.jobID,
          contents: successfulItems.map(({ url }) => ({
            url,
            from: "ai",
            userID: null,
          })),
        })
      );
      if (persistResult?.ok === false) return;

      onOpenGallery?.();
      return;
    }

    await Promise.all(
      successfulItems.map(({ selectionId, url }) =>
        dispatch(
          startBumpAndUpdateContentInList({
            id: selectionId,
            url,
          })
        )
      )
    );

    if (alert?.jobID) {
      await dispatch(markCreativeJobResultsPersisted({ jobID: alert.jobID }));
    }

    onOpenGallery?.();
  };

  if (session.initialItems.length === 0) {
    return null;
  }

  return (
    <ModalBatchImageEdit
      isOpen={true}
      onClose={onClose}
      initialItems={session.initialItems}
      initialBatchImageEditSession={session}
      onCallbackApply={onApplyBatchImageEditResults}
      showSaveAlert={true}
    />
  );
};

const AlertCreativeJobModals = ({
  alertId,
  generationData,
  liveAlert,
  onClose,
  onOpenGallery,
  userID,
}) => {
  const effectiveGenerationData = liveAlert?.generationData ?? generationData;
  const liveJobID = liveAlert?.jobID;
  const liveMessage =
    typeof liveAlert?.message === "string" ? liveAlert.message : "";
  const alertIsTracked = liveAlert != null;
  // The modal's sync effect is idempotent, so a rebuilt generationData object
  // with the same content is safe to pass through.
  const createWithIAInitialValues = useMemo(
    () =>
      buildCreateWithIAInitialValues({
        source: effectiveGenerationData || {},
        jobID: liveJobID,
        alertId,
        message: liveMessage,
        alertIsTracked,
      }),
    [effectiveGenerationData, liveJobID, alertId, liveMessage, alertIsTracked]
  );
  const config = getCreativeJobAlertConfig(liveAlert?.jobType);
  const modalType =
    effectiveGenerationData?.modalType ||
    config.modalType ||
    CREATIVE_ALERT_MODAL_TYPE.createWithIA;

  if (modalType === CREATIVE_ALERT_MODAL_TYPE.imageEdit) {
    return (
      <Suspense fallback={null}>
        <ImageEditFromCreativeAlert
          alert={{ ...liveAlert, generationData: effectiveGenerationData }}
          onClose={onClose}
          onOpenGallery={onOpenGallery}
        />
      </Suspense>
    );
  }

  if (modalType === CREATIVE_ALERT_MODAL_TYPE.imageEditBatch) {
    return (
      <Suspense fallback={null}>
        <BatchImageEditFromCreativeAlert
          alert={{
            ...liveAlert,
            id: alertId,
            generationData: effectiveGenerationData,
          }}
          onClose={onClose}
          onOpenGallery={onOpenGallery}
        />
      </Suspense>
    );
  }

  if (modalType === CREATIVE_ALERT_MODAL_TYPE.gallery) {
    return (
      <Suspense fallback={null}>
        <GalleryFromCreativeAlert
          alert={{
            ...liveAlert,
            id: alertId,
            generationData: effectiveGenerationData,
          }}
          onClose={onClose}
          userID={userID}
        />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={null}>
      <ModalCreateWithIA
        modalOpen={true}
        onCloseModal={onClose}
        initialValues={createWithIAInitialValues}
        onClosePreviousComponents={onClose}
        doReUpdateInitialValues={true}
        onCallbackAfterSaveToGallery={onOpenGallery}
      />
    </Suspense>
  );
};

export default AlertCreativeJobModals;
